import { Injectable } from '@angular/core';

import moment from 'moment-timezone';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { Moment } from 'moment';
import { Tag } from '../models/entities/tag';
import { DateRangeFilter } from '../models/entities/date-range-filter';
import { DateFilter } from '../models/entities/date-filter';

@Injectable({
    providedIn: 'root',
})
export class FilteringService
{
    constructor() {
    }

    /**
     * Get the current moment in the organization's timezone.
     * Falls back to UTC if timezone is not available.
     */
    private getOrgNow(): Moment {
        const timezone = LegFiJwtService.getTimezone() || 'UTC';
        return moment.tz(timezone);
    }

    public setLastMonthForMonthYearRanges(filters: DateRangeFilter[])
    {
        for (const filter of filters) {
            this.setLastMonthForMonthYearRange(filter);
        }
    }

    public setLastMonthForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }) {
        const now = this.getOrgNow();
        const isJan = now.month() === 0;
        if (isJan) {
            filters.startMonth = 12;
            filters.startYear = now.year() - 1;
        } else {
            filters.startMonth = now.month();
            filters.startYear = now.year();
        }

        filters.endMonth = filters.startMonth;
        filters.endYear = filters.startYear;
    }

    public setThisMonthForMonthYearRanges(filters: DateRangeFilter[])
    {
        for (const filter of filters) {
            this.setThisMonthForMonthYearRange(filter);
        }
    }

    public setThisMonthForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }) {
        const now = this.getOrgNow();
        filters.startMonth = now.month() + 1;
        filters.startYear = now.year();
        filters.endMonth = filters.startMonth;
        filters.endYear = filters.startYear;
    }

    public setThisFiscalYearForMonthYearRanges(
            filters: DateRangeFilter[],
            fiscalStartMonth: number,
    ) {
        for (const filter of filters) {
            this.setThisFiscalYearForMonthYearRange(filter, fiscalStartMonth);
        }
    }

    public setThisFiscalYearForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }, fiscalStartMonth: number) {
        const now = this.getOrgNow();
        filters.startMonth = fiscalStartMonth;
        // if NOW is after the fiscal start month, we're in the next budget year (that starts this year)
        // otherwise we are in current and the year started last year
        if (now.month() + 1 >= filters.startMonth) {
            filters.startYear = now.year();
        } else {
            filters.startYear = now.year() - 1;
        }
        if (fiscalStartMonth === 1) {
            filters.endMonth = 12;
            filters.endYear = filters.startYear;
        } else {
            filters.endMonth = fiscalStartMonth - 1;
            filters.endYear = filters.startYear + 1;
        }
    }

    public setLastFiscalYearForMonthYearRanges(
            filters: DateRangeFilter[],
            fiscalStartMonth: number
    ) {
        for (const filter of filters) {
            this.setLastFiscalYearForMonthYearRange(filter, fiscalStartMonth);
        }
    }

    public setLastFiscalYearForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }, fiscalStartMonth: number) {
        const now = this.getOrgNow();
        filters.startMonth = fiscalStartMonth;
        // if NOW is after the fiscal start month, we're in the next budget year (that starts this year)
        // otherwise we are in current and the year started last year
        if (now.month() + 1 >= filters.startMonth) {
            filters.startYear = now.year() - 1;
        } else {
            filters.startYear = now.year() - 2;
        }
        if (fiscalStartMonth === 1) {
            filters.endMonth = 12;
            filters.endYear = filters.startYear;
        } else {
            filters.endMonth = fiscalStartMonth - 1;
            filters.endYear = filters.startYear + 1;
        }
    }

    public setNextFiscalYearForMonthYearRanges(
            filters: DateRangeFilter[],
            fiscalStartMonth: number,
    ) {
        for (const filter of filters) {
            this.setNextFiscalYearForMonthYearRange(filter, fiscalStartMonth);
        }
    }

    public setNextFiscalYearForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }, fiscalStartMonth: number) {
        const now = this.getOrgNow();
        filters.startMonth = fiscalStartMonth;
        // If NOW is after the fiscal start month, we get this fiscal year + 1 (starts next year)
        // otherwise we are in the next year and the new fiscal year starts this year
        if (now.month() + 1 >= filters.startMonth) {
            filters.startYear = now.year() + 1;
        } else {
            filters.startYear = now.year();
        }
        if (fiscalStartMonth === 1) {
            filters.endMonth = 12;
            filters.endYear = filters.startYear;
        } else {
            filters.endMonth = fiscalStartMonth - 1;
            filters.endYear = filters.startYear + 1;
        }
    }

    public setThisCalendarYearForMonthYearRanges(filters: DateRangeFilter[])
    {
        for (const filter of filters) {
            this.setThisCalendarYearForMonthYearRange(filter);
        }
    }

    public setThisCalendarYearForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }) {
        const now = this.getOrgNow();
        filters.startMonth = 1;
        filters.startYear = now.year();
        filters.endMonth = 12;
        filters.endYear = filters.startYear;
    }

    public setLastCalendarYearForMonthYearRanges(filters: DateRangeFilter[])
    {
        for (const filter of filters) {
            this.setLastCalendarYearForMonthYearRange(filter);
        }
    }

    public setLastCalendarYearForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }) {
        const now = this.getOrgNow();
        filters.startMonth = 1;
        filters.startYear = now.year() - 1;
        filters.endMonth = 12;
        filters.endYear = filters.startYear;
    }

    public setNextCalendarYearForMonthYearRanges(filters: DateRangeFilter[])
    {
        for (const filter of filters) {
            this.setNextCalendarYearForMonthYearRange(filter);
        }
    }

    public setNextCalendarYearForMonthYearRange(filters: {
        startMonth: number,
        startYear: number,
        endMonth: number,
        endYear: number
    }) {
        const now = this.getOrgNow();
        filters.startMonth = 1;
        filters.startYear = now.year() + 1;
        filters.endMonth = 12;
        filters.endYear = filters.startYear;
    }

    public setTodayDatePickers(filters: DateFilter[])
    {
        for (const filter of filters) {
            this.setTodayDatePicker(filter);
        }
    }

    public setTodayDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const now = this.getOrgNow();
        filters.startDate = now.clone().startOf('day');
        filters.endDate = now.clone().endOf('day');
    }

    public setLastMonthDatePickers(filters: DateFilter[])
    {
        for (const filter of filters) {
            this.setLastMonthDatePicker(filter);
        }
    }

    public setLastMonthDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const now = this.getOrgNow();
        filters.startDate = now.clone()
                .subtract(1, 'months')
                .startOf('month');
        filters.endDate = now.clone()
                .subtract(1, 'months')
                .endOf('month');
    }

    public setThisMonthDatePickers(filters: DateFilter[])
    {
        for (const filter of filters) {
            this.setThisMonthDatePicker(filter);
        }
    }

    public setThisMonthDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const now = this.getOrgNow();
        filters.startDate = now.clone().startOf('month');
        filters.endDate = now.clone().endOf('month');
    }

    public setNextMonthDatePickers(filters: DateFilter[])
    {
        for (const filter of filters) {
            this.setNextMonthDatePicker(filter);
        }
    }

    public setNextMonthDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const now = this.getOrgNow();
        filters.startDate = now.clone().add(1, 'months').startOf('month');
        filters.endDate = now.clone().add(1, 'months').endOf('month');
    }

    public setNextFiscalYearDatePickers(
            filters: DateFilter[],
            fiscalStartMonth: number,
    ) {
        for (const filter of filters) {
            this.setNextFiscalYearDatePicker(filter, fiscalStartMonth);
        }
    }

    public setNextFiscalYearDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }, fiscalStartMonth: number) {
        const now = this.getOrgNow();
        // if NOW is after the fiscal start month, we're in the next budget year (that starts this year)
        // otherwise we are in current and the year started last year
        if (now.month() + 1 >= fiscalStartMonth) {
            filters.startDate = now.clone()
                    .month(fiscalStartMonth - 1)
                    .add(1, 'years')
                    .startOf('month');
            filters.endDate = now.clone()
                    .month(fiscalStartMonth - 2)
                    .add(2, 'years')
                    .endOf('month');
        } else {
            filters.startDate = now.clone()
                    .month(fiscalStartMonth - 1)
                    .startOf('month');
            filters.endDate = now.clone()
                    .month(fiscalStartMonth - 2)
                    .add(1, 'years')
                    .endOf('month');
        }
    }

    public setThisFiscalYearDatePickers(
            filters: DateFilter[],
            fiscalStartMonth: number,
    ) {
        for (const filter of filters) {
            this.setThisFiscalYearDatePicker(filter, fiscalStartMonth);
        }
    }

    public setThisFiscalYearDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }, fiscalStartMonth: number) {
        const now = this.getOrgNow();
        // if NOW is after the fiscal start month, we're in the next budget year (that starts this year)
        // otherwise we are in current and the year started last year
        if (now.month() + 1 >= fiscalStartMonth) {
            filters.startDate = now.clone()
                    .month(fiscalStartMonth - 1)
                    .startOf('month');
            filters.endDate = now.clone()
                    .month(fiscalStartMonth - 2)
                    .add(1, 'years')
                    .endOf('month');
        } else {
            filters.startDate = now.clone()
                    .month(fiscalStartMonth - 1)
                    .subtract(1, 'years')
                    .startOf('month');
            filters.endDate = now.clone()
                    .month(fiscalStartMonth - 2)
                    .endOf('month');
        }
    }

    public setLastFiscalYearDatePickers(
            filters: DateFilter[],
            fiscalStartMonth: number,
    ) {
        for (const filter of filters) {
            this.setLastFiscalYearDatePicker(filter, fiscalStartMonth);
        }
    }

    public setLastFiscalYearDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }, fiscalStartMonth: number) {
        const now = this.getOrgNow();
        // if NOW is after the fiscal start month, we're in the next budget year (that starts this year)
        // otherwise we are in current and the year started last year
        if (now.month() + 1 >= fiscalStartMonth) {
            filters.startDate = now.clone()
                    .month(fiscalStartMonth - 1)
                    .subtract(1, 'years')
                    .startOf('month');
            filters.endDate = now.clone()
                    .month(fiscalStartMonth - 2)
                    .endOf('month');
        } else {
            filters.startDate = now.clone()
                    .month(fiscalStartMonth - 1)
                    .subtract(2, 'years')
                    .startOf('month');
            filters.endDate = now.clone()
                    .month(fiscalStartMonth - 2)
                    .subtract(1, 'years')
                    .endOf('month');
        }
    }

    public setNextCalendarYearDatePickers(filters: DateFilter[])
    {
        for (const filter of filters) {
            this.setNextCalendarYearDatePicker(filter);
        }
    }

    public setNextCalendarYearDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const now = this.getOrgNow();
        filters.startDate = now.clone()
                .month(0)
                .add(1, 'years')
                .startOf('month');
        filters.endDate = now.clone()
                .month(11)
                .add(1, 'years')
                .endOf('month');
    }

    public setThisCalendarYearDatePickers(filters: DateFilter[])
    {
        for (const filter of filters) {
            this.setThisCalendarYearDatePicker(filter);
        }
    }

    public setThisCalendarYearDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const now = this.getOrgNow();
        filters.startDate = now.clone()
                .month(0)
                .startOf('month');
        filters.endDate = now.clone()
                .month(11)
                .endOf('month');
    }

    public setLastCalendarYearDatePickers(filters: DateFilter[])
    {
        for (const filter of filters) {
            this.setLastCalendarYearDatePicker(filter);
        }
    }

    public setLastCalendarYearDatePicker(filters: {
        startDate: Moment,
        endDate: Moment
    }) {
        const now = this.getOrgNow();
        filters.startDate = now.clone()
                .month(0)
                .subtract(1, 'years')
                .startOf('month');
        filters.endDate = now.clone()
                .month(11)
                .subtract(1, 'years')
                .endOf('month');
    }

    public normalizeMixedDataValueForSorting(value) {
        const padding = '000000000000000';

        if (!value) {
            return '';
        }

        if (moment.isMoment(value)) {
            return value.format();
        }

        value = value.toString();
        value = value.replace(
                /(\d+)((\.\d+)+)?/g,
                ($0, integer, decimal, $3) => {
                    if (decimal !== $3) {
                        return padding.slice(integer.length) + integer + decimal;
                    }
                    decimal = decimal || '.0';

                    return (
                            padding.slice(integer.length) +
                            integer +
                            decimal +
                            padding.slice(decimal.length)
                    );
                },
        );
        return value.toLowerCase();
    }

    public matTableTaggableSearch(originalFilterFunction, filterString, filterTags: Tag[], data: { tags: Tag[] }) {
        const matchesTextFilter = originalFilterFunction(data, filterString);
        if (!matchesTextFilter) {
            return false;
        }
        if (filterTags.length === 0) {
            return true;
        }
        let matchesTagFilter = true;
        filterTags.forEach(tag => {
            if (!matchesTagFilter) {
                return false;
            }

            if (tag?.filter === 'not_equals') {
                if (data.tags.map(t => t.tag).includes(tag.tag)) {
                    matchesTagFilter = false;
                }
            } else {
                if (!data.tags.map(t => t.tag).includes(tag.tag)) {
                    matchesTagFilter = false;
                }
            }
        });
        return matchesTagFilter;
    }

    public extractPath(path: string, obj: any, separator: string = '.'): any {
        // Special path handling for payable table columns
        if (path === 'vendor') {
            return obj.vendor ? obj.vendor.name : '';
        }
        if (path === 'approvals') {
            if (obj.numberRequired > 0) {
                return `${obj.approvedCount} of ${obj.numberRequired} Approved`;
            }
            else if (obj.numberRequired === 0 && obj.currentFulfillment && obj.currentFulfillment.statusDescription !== 'Draft') {
                return 'Approval Not Required';
            }
            return '';
        }
        if (path === 'statusDescription') {
            return obj.statusDescription || '';
        }

        const properties = Array.isArray(path) ? path : path.split(separator);
        // @ts-ignore
        return properties.reduce((prev, curr) => prev && prev[curr], obj);
        // Extract nested values from 'obj' using the keys in 'properties'.
        // If any property in the chain is missing, it returns undefined to prevent runtime errors.
    }
}
